import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';
import { X, ChevronRight } from 'lucide-react';
import { BRANDS } from './Topbar';

/**
 * Sidebar — persistent grouped navigation.
 *
 * Dark rail on desktop; an off-canvas drawer below 1024px, driven by the
 * `open` prop from the shell.
 *
 * Groups collapse. With 47 entries a flat list was the main source of
 * friction — the group holding the current page is always open, and the rest
 * are the reader's to choose.
 */

/** Which group owns this path — that group is force-opened. */
const groupForPath = (groups, pathname) =>
  groups.find((group) =>
    group.items.some((item) => pathname.endsWith(`/${item.slug}`)),
  )?.id ?? null;

const Sidebar = ({ groups, open, onClose, basePath }) => {
  const { pathname } = useLocation();
  const activeGroup = groupForPath(groups, pathname);

  // Overview belongs to no group, so without a fallback every group would sit
  // collapsed and the rail would read as an empty accordion.
  const [expanded, setExpanded] = useState(
    () => new Set([activeGroup ?? groups.find((g) => g.label)?.id].filter(Boolean)),
  );

  // Navigating into a collapsed group opens it — otherwise the active item
  // would be hidden behind a closed header.
  useEffect(() => {
    if (!activeGroup) return;
    setExpanded((prev) => (prev.has(activeGroup) ? prev : new Set(prev).add(activeGroup)));
  }, [activeGroup]);

  const toggle = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  return (
    <aside
      className={`ds-page__sidebar${open ? ' ds-page__sidebar--open' : ''}`}
      aria-label="Design system navigation"
    >
      <div className="ds-page__brand">
        <span className="ds-page__brand-mark">
          <span className="typo-h5 ds-page__brand-title">Design System</span>
          <span className="typo-label ds-page__brand-sub">{BRANDS.join(' · ')}</span>
        </span>
        <button
          type="button"
          onClick={onClose}
          className="ds-page__menu-btn"
          aria-label="Close navigation"
        >
          <X size={18} />
        </button>
      </div>

      <nav className="ds-page__nav" aria-label="Components">
        {groups.map((group) => {
          // An unlabelled group (Overview) has no header to collapse from.
          const collapsible = Boolean(group.label);
          const isOpen = !collapsible || expanded.has(group.id);

          return (
            <div key={group.id} className="ds-page__nav-group">
              {collapsible && (
                <button
                  type="button"
                  className="typo-h5 ds-page__nav-group-label"
                  onClick={() => toggle(group.id)}
                  aria-expanded={isOpen}
                  aria-controls={`ds-nav-${group.id}`}
                >
                  {group.label}
                  <ChevronRight
                    size={12}
                    aria-hidden="true"
                    className={`ds-page__nav-chevron${isOpen ? ' ds-page__nav-chevron--open' : ''}`}
                  />
                </button>
              )}

              <ul className="ds-page__nav-list" id={`ds-nav-${group.id}`} hidden={!isOpen}>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.slug}>
                      <NavLink
                        to={`${basePath}/${item.slug}`}
                        onClick={onClose}
                        className={({ isActive }) =>
                          `typo-body ds-page__nav-link${isActive ? ' ds-page__nav-link--active' : ''}`
                        }
                      >
                        {Icon && <Icon size={16} className="ds-page__nav-icon" aria-hidden="true" />}
                        <span className="ds-page__nav-label">{item.label}</span>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

Sidebar.propTypes = {
  /** Nav groups from the registry */
  groups: PropTypes.array.isRequired,
  /** Drawer open state (mobile only) */
  open: PropTypes.bool,
  /** Close the drawer — also fires on every nav click */
  onClose: PropTypes.func,
  /** Route prefix, e.g. "/design-system" */
  basePath: PropTypes.string.isRequired,
};

export default Sidebar;
